import { useTranslation } from "react-i18next";
import TitleSmall from "../../../atoms/typography/TitleSmall";
import { useInvoiceDuplicates } from "../../../../graphql/hooks/invoices/useInvoiceDuplicates";
import LoadingSpinner from "../../../atoms/LoadingSpinner";
import Alert from "../../Alert";
import BodySmall from "../../../atoms/typography/BodySmall";
import { theme } from "../../../../theme";
import { HorizontalSpacer } from "../../../atoms/Spacers";
import TableHeader from "../../../atoms/TableHeader";
import TableHeaderText from "../../../atoms/typography/TableHeaderText";
import { Col } from "react-grid-system";
import TableRow from "../../../atoms/TableRow";
import { getInvoiceListStatusBadge } from "../../../../utils/invoicesHelpers";
import dayjs from "dayjs";
import { formatPrice, getCurrencySymbol } from "../../../../utils/formatters";
import DeleteInvoicesButton from "../../invoices/DeleteInvoicesButton";
import { onClickGoToInvoice } from "../../../../utils/goToInvoice";
import { useLocation, useNavigate } from "react-router-dom";

interface Props {
  clientIdentifier: string;
  invoiceId: string;
}

const DuplicatedInvoiceNumberListModalContent: React.FC<Props> = ({
  clientIdentifier,
  invoiceId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { invoice, error, loading } = useInvoiceDuplicates(
    clientIdentifier,
    invoiceId,
  );

  return (
    <div>
      <TitleSmall color={theme.colors.primary}>
        {t("invoices.issuerHasDuplicatedInvoices")} ({invoice?.invoiceNumber})
      </TitleSmall>
      <HorizontalSpacer spacing={1} />
      {loading && <LoadingSpinner />}
      {error && (
        <Alert
          type="error"
          title={t("errors.duplicatedInvoicesCouldNotBeFetched")}
          message={error?.message}
        />
      )}
      <TableHeader align="center" nogutter>
        <Col lg={1.5}>
          <TableHeaderText>{t("invoices.invoice")}</TableHeaderText>
        </Col>
        <Col lg={2}>
          <TableHeaderText>{t("common.status")}</TableHeaderText>
        </Col>

        <Col lg={2}>
          <TableHeaderText>{t("common.recipient")}</TableHeaderText>
        </Col>
        <Col lg={1.5}>
          <TableHeaderText> {t("documentHandler.registered")}</TableHeaderText>
        </Col>
        <Col lg={1.5}>
          <TableHeaderText>{t("invoices.invoiceDate")}</TableHeaderText>
        </Col>
        <Col lg={1.5}>
          <TableHeaderText>{t("invoices.dueDate")}</TableHeaderText>
        </Col>
        <Col lg={1.5}>
          <TableHeaderText>{t("invoices.amount")}</TableHeaderText>
        </Col>
        <Col lg={0.5} />
      </TableHeader>
      {invoice?.duplicateInvoices?.map((dupe) => (
        <TableRow
          key={dupe.id}
          align="center"
          onClick={() =>
            onClickGoToInvoice(
              dupe.id,
              dupe.state?.processStatus ?? "UNKNOWN",
              clientIdentifier,
              navigate,
              location,
            )
          }
        >
          <Col lg={1.5}>
            <BodySmall>{dupe.invoiceNumber}</BodySmall>
          </Col>
          <Col lg={2}>
            {getInvoiceListStatusBadge(dupe.state?.processStatus ?? "UNKNOWN")}
          </Col>
          <Col lg={2}>
            <BodySmall>{dupe.invoiceRecipient?.name}</BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>
              {dayjs(dupe?.createdAt).format("YYYY-MM-DD HH:mm")}
            </BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>
              {dayjs(dupe?.invoiceDate).format("YYYY-MM-DD HH:mm")}
            </BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>
              {dayjs(dupe?.dueDate).format("YYYY-MM-DD HH:mm")}
            </BodySmall>
          </Col>
          <Col lg={1.5}>
            <BodySmall>
              {formatPrice(dupe?.totalAmountWithVat ?? 0)}{" "}
              {getCurrencySymbol(dupe?.currencyCode) ?? ""}
            </BodySmall>
          </Col>
          <Col lg={0.5}>
            <div onClick={(e) => e.stopPropagation()}>
              <DeleteInvoicesButton invoiceIds={[dupe.id]} buttonType="circle" />
            </div>
          </Col>
        </TableRow>
      ))}
    </div>
  );
};

export default DuplicatedInvoiceNumberListModalContent;
